import { legalConfig as cfg } from '../legalConfig';
import { LegalDoc } from '../types';

export const privacyDoc: LegalDoc = {
  slug: 'privacy',
  title: 'Privacy Policy',
  effectiveDate: cfg.effectiveDate,
  lastUpdated: cfg.lastUpdated,
  sections: [
    {
      heading: '1. Purpose and scope',
      blocks: [
        {
          type: 'p',
          text: `This Privacy Policy explains how ${cfg.entityName}, trading as ${cfg.brandName}, collects, uses, shares, stores, and protects personal information when you use the ${cfg.brandName} website, mobile application, support channels, and related services.`,
        },
        {
          type: 'p',
          text: `For purposes of the Protection of Personal Information Act 4 of 2013 (POPIA), ${cfg.entityName} is the responsible party for the processing described in this Policy.`,
        },
      ],
    },
    {
      heading: '2. Personal information we collect',
      blocks: [
        { type: 'p', text: `Depending on how you use ${cfg.brandName}, we may collect:` },
        { type: 'p', text: 'Account and contact information:' },
        {
          type: 'ul',
          items: [
            'name, email address, telephone number, date of birth or age confirmation;',
            'login, authentication, and account status information; and',
            'communication preferences.',
          ],
        },
        { type: 'p', text: 'Profile and matching information:' },
        {
          type: 'ul',
          items: [
            'profile photographs, biography, occupation or student status;',
            'preferred area, approximate location, budget, move date, and housing needs;',
            'lifestyle and household preferences, such as smoking, pets, noise, guests, schedules, cleanliness, and shared responsibilities; and',
            'profiles viewed, likes, passes, matches, and compatibility preferences.',
          ],
        },
        { type: 'p', text: 'Property and landlord information:' },
        {
          type: 'ul',
          items: [
            'property descriptions, photographs, approximate or exact address as appropriate, rent, deposit, availability, rules, and amenities;',
            'landlord or representative identity, contact information, role, and evidence of authority where requested; and',
            'listing and invitation activity.',
          ],
        },
        { type: 'p', text: 'Communications and safety information:' },
        {
          type: 'ul',
          items: [
            'chats, attachments, landlord invitations, support requests, reports, blocks, moderation decisions, and information supplied during an investigation; and',
            'records reasonably needed to address fraud, safety, legal claims, or Platform misuse.',
          ],
        },
        { type: 'p', text: 'Device and usage information:' },
        {
          type: 'ul',
          items: [
            'IP address, device and browser type, operating system, app version, language, timestamps, crash data, security logs, and identifiers; and',
            'cookie and similar technology information, subject to your choices.',
          ],
        },
        {
          type: 'p',
          text: `Verification or payment information: If ${cfg.brandName} later offers identity verification or paid services, we may receive verification status, limited identity details, transaction references, subscription status, and payment metadata from approved providers. We do not store full payment card details unless specifically required and supported by appropriate payment security controls.`,
        },
      ],
    },
    {
      heading: '3. Special personal information',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} does not require users to disclose special personal information such as health information, religious or philosophical beliefs, political views, trade union membership, race or ethnic origin, sex life, criminal behaviour, or biometric information unless a specific feature clearly requires it and we have confirmed a lawful basis and appropriate safeguards.`,
        },
        {
          type: 'p',
          text: "Do not place identity documents, financial records, medical details, intimate information, or another person's sensitive information in a public profile or ordinary chat.",
        },
      ],
    },
    {
      heading: '4. Where information comes from',
      blocks: [
        { type: 'p', text: 'We collect information:' },
        {
          type: 'ul',
          items: [
            'directly from you;',
            'from other users when they invite you, communicate with you, list you as a property representative, or report an interaction;',
            'automatically from your device and use of the Platform; and',
            'from service providers or public sources where lawful and reasonably necessary for verification, fraud prevention, support, or safety.',
          ],
        },
      ],
    },
    {
      heading: '5. Why we process personal information',
      blocks: [
        { type: 'p', text: 'We process personal information to:' },
        {
          type: 'ul',
          items: [
            'create and manage accounts;',
            'provide profiles, discovery, matching, chat, landlord conversations, and support;',
            'personalise and improve recommendations and user experience;',
            'verify information where a verification feature is offered;',
            'protect users, investigate reports, prevent fraud, and secure the Platform;',
            'communicate service messages and, with the required choice or consent, marketing;',
            'analyse performance, fix errors, and develop features using aggregated or de-identified information where possible;',
            'enforce our Terms and Community Guidelines; and',
            'comply with law, legal process, and valid requests from authorities.',
          ],
        },
        {
          type: 'p',
          text: `Under POPIA, our justification may include your consent, performance of an agreement with you, compliance with a legal obligation, protection of your or another person's legitimate interests, or pursuit of ${cfg.brandName}'s legitimate interests where your rights do not override them.`,
        },
      ],
    },
    {
      heading: '6. Matching and profiling',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} may compare information such as location preference, budget, move date, lifestyle choices, and Platform activity to suggest or order profiles and listings. This is profiling used to provide discovery features.`,
        },
        {
          type: 'p',
          text: `A suggestion does not approve or reject a lease and does not create a decision with legal effect. You decide whom to contact, meet, invite, or live with. You may update matching preferences and can contact ${cfg.privacyEmail} for information about how recommendations work or to raise a concern.`,
        },
        {
          type: 'p',
          text: 'We do not use solely automated processing to make a decision that has legal consequences or affects a person to a substantial degree unless the processing is lawful and appropriate protections are in place.',
        },
      ],
    },
    {
      heading: '7. Who can see your information',
      blocks: [
        {
          type: 'p',
          text: 'Other users: Information you add to a visible profile or listing can be seen by users within the relevant discovery settings. Do not make exact home addresses, identity numbers, financial details, or private contact information publicly visible.',
        },
        {
          type: 'p',
          text: 'Matched users and chat participants: People in a chat can see content sent to that chat. Users may save or share information outside the Platform despite our rules, so use care with what you disclose.',
        },
        {
          type: 'p',
          text: 'Landlords in an inquiry conversation: When a property inquiry conversation is created, the matched pair and the relevant landlord or representative can see the messages and files sent within that conversation. Your private roommate chat is kept separate and is not disclosed to the landlord.',
        },
        {
          type: 'p',
          text: 'Service providers: We may use carefully selected providers for hosting, storage, authentication, email, customer support, analytics, security, moderation, verification, and payments. They may process information only for authorised purposes and under appropriate contractual and security obligations.',
        },
        {
          type: 'p',
          text: 'Legal, safety, and corporate disclosures: We may disclose information where reasonably necessary to comply with law or valid legal process, protect a person from harm, investigate fraud or abuse, establish or defend legal rights, or complete a genuine corporate transaction subject to appropriate protections.',
        },
        {
          type: 'p',
          text: `${cfg.brandName} does not sell personal information. If this practice changes, the Policy and user choices will be updated before the change begins.`,
        },
      ],
    },
    {
      heading: '8. International processing',
      blocks: [
        {
          type: 'p',
          text: `Some service providers may process information outside ${cfg.launchMarket}. Where this happens, ${cfg.brandName} will use a lawful transfer mechanism and take reasonably practicable steps to ensure the recipient is subject to an adequate law, binding rules, agreement, consent, or another basis permitted by POPIA.`,
        },
        {
          type: 'p',
          text: `Our infrastructure and service providers currently process information in: ${cfg.processingCountries}.`,
        },
      ],
    },
    {
      heading: '9. Retention and deletion',
      blocks: [
        {
          type: 'p',
          text: 'We keep personal information only for as long as reasonably needed for the purpose for which it was collected, or as required for safety, dispute resolution, fraud prevention, legal claims, and compliance.',
        },
        {
          type: 'p',
          text: 'Our retention schedule is tied to the purpose of each type of information:',
        },
        {
          type: 'ul',
          items: [
            'active account and profile information: while the account is active;',
            'deleted account and ordinary profile data: deleted or de-identified within 30 days, unless another period is required by law;',
            'backup copies: expire within 90 days where technically practicable;',
            'chats and inquiry conversations: kept while the related match or inquiry is active, and deleted within 12 months after it becomes inactive or the account is deleted;',
            'reports, blocks, serious safety records, and related evidence: up to three years after the matter closes, or longer if reasonably required for an active claim or legal obligation;',
            'security logs: 12 months; and',
            'payment and transaction records, if paid services are added: retained for the period required by applicable tax, accounting, electronic transaction, and consumer laws (generally at least five years in South Africa).',
          ],
        },
        {
          type: 'p',
          text: 'Deleting an account does not delete copies another user has already received or information we must lawfully retain. When retention ends, we delete, destroy, or de-identify the information in a reasonably secure manner.',
        },
      ],
    },
    {
      heading: '10. Security',
      blocks: [
        {
          type: 'p',
          text: 'We use appropriate, reasonable technical and organisational measures designed to protect personal information against loss, damage, unauthorised access, and unlawful processing. Measures include access controls, encryption where appropriate, secure software development, monitoring, backups, staff or contractor confidentiality, supplier reviews, and incident response.',
        },
        {
          type: 'p',
          text: `No service can guarantee absolute security. If you believe your account or information is at risk, contact ${cfg.securityEmail} immediately.`,
        },
        {
          type: 'p',
          text: `Where there are reasonable grounds to believe that personal information has been accessed or acquired by an unauthorised person, ${cfg.brandName} will follow applicable notification requirements for the Information Regulator and affected people.`,
        },
      ],
    },
    {
      heading: '11. Your privacy rights',
      blocks: [
        { type: 'p', text: `Subject to applicable law, you may ask ${cfg.brandName} to:` },
        {
          type: 'ul',
          items: [
            'confirm whether we hold personal information about you;',
            'provide access to that information;',
            'correct or update inaccurate or incomplete information;',
            'delete or destroy information that we are no longer authorised to retain;',
            'restrict processing where appropriate;',
            'record an objection to processing;',
            'stop direct marketing or withdraw marketing consent; and',
            'explain or review relevant automated processing where applicable.',
          ],
        },
        {
          type: 'p',
          text: `Send a request to ${cfg.privacyEmail}. We may need to verify your identity before acting. We will not ask for more information than reasonably needed for verification.`,
        },
        {
          type: 'p',
          text: `You may also lodge a complaint with the Information Regulator of South Africa. Current contact and complaint information is available at ${cfg.infoRegulatorUrl}.`,
        },
      ],
    },
    {
      heading: '12. Direct marketing',
      blocks: [
        {
          type: 'p',
          text: 'Service messages about an account, match, chat, security issue, policy change, or support request are not marketing.',
        },
        {
          type: 'p',
          text: `${cfg.brandName} will send electronic direct marketing only where permitted by law. Marketing consent must be a separate, optional choice and must not be preselected. Every marketing message will identify ${cfg.brandName} and provide a simple way to opt out. Opting out of marketing does not stop necessary service messages.`,
        },
      ],
    },
    {
      heading: '13. Cookies and analytics',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} may use essential cookies or similar technologies for login, security, preferences, and core Platform functions. Non-essential analytics, advertising, or personalisation technologies are used only after the required notice and choice.`,
        },
        {
          type: 'p',
          text: 'You can revisit cookie choices at any time through the "Cookie settings" link. See the Cookie Notice for more detail.',
        },
      ],
    },
    {
      heading: '14. Children',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} is for people aged 18 and older. We do not knowingly allow children to create accounts. If you believe a person under 18 has provided personal information, contact ${cfg.privacyEmail} so that we can investigate and take appropriate action.`,
        },
      ],
    },
    {
      heading: '15. Changes to this Policy',
      blocks: [
        {
          type: 'p',
          text: 'We may update this Policy to reflect legal, technical, or service changes. If a change materially affects how we use personal information, we will give reasonable notice before it takes effect where required. The latest version will always show the effective date.',
        },
      ],
    },
    {
      heading: `16. Contact ${cfg.brandName}`,
      blocks: [
        {
          type: 'ul',
          items: [
            `Responsible party: ${cfg.entityName}`,
            `Privacy requests: ${cfg.privacyEmail}`,
            `General support: ${cfg.supportEmail}`,
          ],
        },
      ],
    },
  ],
};
